<template>
    <teleport to="body">
        <transition name="modal-fade" appear @enter="onEnter" @leave="onLeave">
            <div 
                v-if="showModal" 
                class="modal" 
                :class="{ show: showModal }" 
                @click.self="closeModal"
            >
                <div class="modal__dialog modal__dialog--xl modal__dialog__profile" :class="{ 'modal__dialog--md': accountPlan !== 'plus' }">
                    <div role="document" class="relative h--100">
                        <div class="modal__body" :class="{ 'flex align-items-center': accountPlan !== 'plus', 'disabled': pageState === 'save_updates_loading' }">
                            <template v-if="accountPlan !== 'plus'">
                                <EmptyPage 
                                    state="empty_custom" 
                                    btn-state="custom"
                                    icon-name="save"
                                    title="This is a paid feature"
                                    subtitle="Upgrade to a plus plan to get access to this feature" 
                                >
                                    <template #button>
                                        <BaseButton 
                                            label="Upgrade account" 
                                            @submit="closeAndOpenBillings"
                                        />    
                                    </template>
                                </EmptyPage>
                            </template>
                            <template v-else>
                                <div class="w-100 relative h--100">
                                    <div class="profile-sidebar" :class="{ 'disabled': isFeatDisabled }">
                                        <section class="p-6 md:p-8 pb--0 pl--10">
                                            <p class="mb--15 text--sm text--medium">
                                                Select profile data to export
                                            </p> 
        
                                            <div v-for="item in profileSelections" :key="item.key" class="form-check mb--5">
                                                <input 
                                                    :id="`${item.label}Check`" v-model="selectedFields" :value="item.key"
                                                    class="form-check-input" type="checkbox" :checked="checkboxMap[item.key]" 
                                                    :disabled="item.disabled"
                                                    @change="checkboxMap = { ...checkboxMap, [item.key]: $event.target.checked }"
                                                >
                                                <label class="form-check-label text--sm" for="checkDefault">
                                                    {{ item.label }}
                                                </label>
        
                                                <div v-if="item.children && item.children.length" class="pl--15 pr--15 mb--10">
                                                    <div v-for="inner in item.children" :key="inner.key">
                                                        <input 
                                                            :id="`${inner.label}Check`" v-model="selectedFields" :value="inner.key"
                                                            class="form-check-input" type="checkbox" :disabled="inner.disabled"
                                                            :checked="checkboxMap[inner.key]" @change="checkboxMap = { ...checkboxMap, [inner.key]: $event.target.checked }"
                                                        >
                                                        <label class="form-check-label text--sm" for="checkDefault">
                                                            {{ inner.label }}
                                                        </label>
                                                    </div>
                                                </div>
                                            </div>
        
                                            <div v-if="publicProfile?.isActive || profileUrl" class="p-6 md:p-8 pb--0 pl--10">
                                                <p class="mb--15 text--sm text--medium">
                                                    Others
                                                </p> 
        
                                                <div v-if="publicProfile?.isActive" class="form-check mb--5">
                                                    <input 
                                                        id="profileImageCheck`" v-model="pageActive" :value="pageActive"
                                                        class="form-check-input" type="checkbox" 
                                                        :checked="pageActive"
                                                    >
                                                    <label class="form-check-label text--sm" for="checkDefault">
                                                        Enable page view
                                                    </label>
                                                </div>
            
                                                <div v-if="profileUrl" class="form-check mb--5">
                                                    <input 
                                                        id="profileImageCheck`" v-model="useProfileImg"
                                                        class="form-check-input" type="checkbox" 
                                                    >
                                                    <label class="form-check-label text--sm" for="checkDefault">
                                                        {{ !useProfileImg ? 'Use profile image' : 'Use avatar text' }}
                                                    </label>
                                                </div>
                                            </div>
                                        </section>
        
                                        <div class="mt--20 pr--10">
                                            <BaseButton 
                                                label="Save updates"
                                                full-width
                                                class="text--sm"
                                                variant="outline"
                                                :disabled="pageState === 'save_updates_loading' || isFeatDisabled"
                                                @submit="saveProfile"
                                            />
                                        </div>
                                    </div>
                                       
                                    <div class="profile-content flex-2">
                                        <div class="flex align-items-center justify-content-between mb--25">
                                            <p class="text--md text--semi--bold mb--15">
                                                Export preview
                                            </p>
                                                
                                            <div class="modal__header modal__dialog--header">
                                                <button class="close" @click="closeModal">
                                                    <svg
                                                        xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                                                        viewBox="0 0 24 24" style="fill: rgba(160, 158, 167, 1);transform: ;msFilter:;"
                                                    >
                                                        <path d="m16.192 6.344-4.243 4.242-4.242-4.242-1.414 1.414L10.535 12l-4.242 4.242 1.414 1.414 4.242-4.242 4.243 4.242 1.414-1.414L13.364 12l4.242-4.242z" />
                                                    </svg>
                                                </button>
                                            </div>
                                        </div>
    
                                        <template v-if="pageState === 'data_loading'">
                                            <PageSpinner />
                                        </template>
                                    
                                        <template v-else-if="pageState === 'data_error'">
                                            <div class="mt--40">
                                                <EmptyPage 
                                                    state="error_custom" 
                                                    :btn-state="pageErrMsg.title === 'This is a paid feature' ? 'none' : 'custom' "
                                                    :title="pageErrMsg.title"
                                                    :subtitle="pageErrMsg.sub" 
                                                    @action="handleFetchDataErr" 
                                                />
                                            </div>
                                        </template>

                                        <template v-else-if="isFeatDisabled">
                                            <div class="mt--40">
                                                <EmptyPage 
                                                    state="empty_custom" 
                                                    btn-state="custom"
                                                    icon-name="store-alt"
                                                    title="Feature disabled"
                                                    subtitle="Page has been disabled. Enable page view to continue" 
                                                    @action="handleFetchDataErr" 
                                                >
                                                    <template #button>
                                                        <BaseButton 
                                                            label="Enable public view"
                                                            prefix-icon
                                                            icon-name="show"
                                                            class="text--sm"
                                                            @submit="saveProfile(true)"
                                                        />
                                                    </template>
                                                </EmptyPage>
                                            </div>
                                        </template>
                
                                        <template v-else>
                                            <div class="main-container w-100">
                                                <section class="profile-wrap p-6 md:p-8 pb--0">
                                                    <div class="profile-card flex flex-col align-items-center">
                                                        <div>
                                                            <CustomAvatar
                                                                v-if="useProfileImg"
                                                                border-style="solid" 
                                                                width="100px" 
                                                                :initials="initials" 
                                                                :src="profileUrl"
                                                                class="text--lg"
                                                                show-upload
                                                            />
                                                            <div v-else class="profile-icon">
                                                                {{ initials }}
                                                            </div>
                                                        </div>
            
                                                        <h1 v-if="checkboxMap['fullName']" class="profile-title">
                                                            {{ profile?.fullName }}
                                                        </h1>
            
                                                        <div class="mb--5">
                                                            <span v-if="checkboxMap['email']" class="profile-stats">{{ profile?.email }}</span>
                                                            <span v-if="checkboxMap['email'] && checkboxMap['occupation']" class="profile-stats ml--3 mr--3">|</span>
                                                            <span v-if="checkboxMap['occupation']" class="text--capitalize profile-stats">{{ profile?.occupation }}</span>
                                                        </div>
            
                                                        <div class="flex align-items-center">
                                                            <p v-if="checkboxMap['completedProjectCount']" class="profile-stats">
                                                                {{ publicProfile?.completedProjectCount }} projects completed
                                                            </p> 
                                                            <p v-if="checkboxMap['completedProjectCount'] && checkboxMap['onboardedClientsCount']" class="profile-stats ml--3 mr--3">
                                                                |
                                                            </p>
                                                            <p v-if="checkboxMap['onboardedClientsCount']" class="profile-stats">
                                                                {{ publicProfile?.onboardedClientsCount }} clients onboarded
                                                            </p>
                                                        </div>

                                                        <header v-if="publicShareUrl" class="py-2 px-3 flex align-items-center justify-content-between z_p_header">
                                                            <div class="flex align-items-center mr--15">
                                                                <span class="ml-2 text--sm text-brown-100 mb--0">{{ publicShareUrl }}</span>
                                                            </div>
                   
                                                            <div class="flex items-center space-x-2">
                                                                <div class="dropdown">
                                                                    <BaseButton 
                                                                        label="Share"
                                                                        prefix-icon
                                                                        class="text--sm"
                                                                        variant="outline"
                                                                        icon-name="share"
                                                                        aria-expanded="false"
                                                                        data-bs-toggle="dropdown" 
                                                                        data-bs-auto-close="true" 
                                                                    />

                                                                    <List id="noteDropdown">
                                                                        <ListItem flex pointer @click="handleCopyNoteContent(item)">
                                                                            <IconSvg class="mr--10" name="copy" fill="#64748B" /> 
                                                                            <span class="mb--0">Copy link</span>
                                                                        </ListItem>
                                                                        <ListItem flex pointer>
                                                                            <IconSvg class="mr--10" name="whatsapp" fill="#64748B" /> 
                                                                            <span class="mb--0">Share via WhatsApp</span>
                                                                        </ListItem>
                                                                        <ListItem flex pointer>
                                                                            <IconSvg class="mr--10" name="instagram" fill="#64748B" /> 
                                                                            <span class="mb--0">Share via Instagram</span>
                                                                        </ListItem>
                                                                        <ListItem flex pointer>
                                                                            <IconSvg class="mr--10" name="x" fill="#64748B" />
                                                                            <span class="mb--0">Share via X</span>
                                                                        </ListItem>
                                                                        <ListItem flex pointer>
                                                                            <IconSvg class="mr--10" name="linkedin" fill="#64748B" /> 
                                                                            <span class="mb--0">Share via LinkedIn</span>
                                                                        </ListItem>
                                                                    </List>
                                                                </div>
                                                            </div>
                                                        </header>
                                                    </div>
                                                </section>
                                      
                                                <section class="profile-wrap p-6 md:p-8">
                                                    <div v-if="checkboxMap['bio']" class="profile-card">
                                                        <h3>
                                                            Bio
                                                        </h3>
                                                        <p>
                                                            {{ profile?.bio }}
                                                        </p>
                                                    </div>
                   
                                                    <div v-if="checkboxMap['location']" class="profile-card">
                                                        <h3>
                                                            Location
                                                        </h3>
                                                        <p>
                                                            {{ profile?.city }}, {{ profile?.country }}
                                                        </p>
                                                    </div>
                   
                                                    <div class="profile-card">
                                                        <h3>
                                                            Portfolio
                                                        </h3>
                   
                                                        <template v-if="checkboxMap['socials']">
                                                            <div v-if="profile?.socials && profile.socials.length">
                                                                <p class="text--sm text-gray-600 mb--10">
                                                                    Social links
                                                                </p>
                                                                <div class="flex space-x-3 flex-wrap">
                                                                    <a 
                                                                        v-for="item in profile.socials" 
                                                                        :key="item._id" 
                                                                        :href="item.url" 
                                                                        target="_blank" 
                                                                        class="p-2 border border-dark pointer rounded-full transition duration-150 mr--15"
                                                                    >
                                                                        <IconSvg 
                                                                            class="h-5 w-5 text-gray-700" 
                                                                            fill="#4a3b31" 
                                                                            :name="item.name" 
                                                                            width="24px" 
                                                                            height="24px" 
                                                                        />
                                                                    </a>
                                                                </div>
                                                            </div>
                                                        </template>
                   
                                                        <template v-if="checkboxMap['skills']">
                                                            <div v-if="profile?.skills && profile.skills.length" class="mt--15">
                                                                <p class="text--sm text-gray-600">
                                                                    Skills
                                                                </p>
                                                                <div class="flex flex-wrap justify-center gap-2 mt--15">
                                                                    <span 
                                                                        v-for="skill in profile.skills" 
                                                                        :key="skill" 
                                                                        class="tag text--xs px-3 py-1 rounded-full text-white"
                                                                    >
                                                                        {{ skill }}
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </template>
                   
                                                        <template v-if="checkboxMap['links']">
                                                            <div v-if="profile?.links && profile.links.length" class="mt--20">
                                                                <p class="text--sm text-gray-600 mb--10">
                                                                    Links
                                                                </p>
                   
                                                                <a v-for="link in profile.links" :key="link._id" href="#" class="text-gray-200 text--underline profile-links flex items-center pt-1 pb-1 text--sm transition duration-150">
                                                                    <span class="text-sm md:text-base font-medium mb--5">{{ link.name }}</span>
                                                                </a>
                                                            </div>
                                                        </template>
                                                    </div>
                                                </section>
                                            </div>
                                        </template>
                                    </div>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>
            </div>
        </transition>
    </teleport>
</template>

<script>
import BaseButton from "../buttons/BaseButton.vue";
import IconSvg from "../icons/Icon-Svg.vue";
import envs from "@/config/env";
import { profileSelection } from "@/components/dataComponents/account";
import CustomAvatar from "../avatar/CustomAvatar.vue";
import EmptyPage from "../emptyPage/EmptyPage.vue";
import PageSpinner from "../loaders/PageSpinner.vue";
import List from "../lists/List.vue";
import ListItem from "../lists/ListItem.vue";

export default {
    name: "CurateProfile",

    components: {
        BaseButton,
        IconSvg,
        CustomAvatar,
        EmptyPage,
        PageSpinner,
        List,
        ListItem,
    },

    props: {
        currentSelection: {
            type: String,
            default: "",
        },
        showModal: {
            type: Boolean,
            default: false,
        },
        loading: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["cancel"],

    data() {
        return {
            pageState: "",
            selectedFields: [],
            profileSelection,
            profileSelections: this.transformDataForChecklist(profileSelection),
            useProfileImg: false,
            pageActive: false,
            errMsg: {},
            isFeatDisabled: false,
        };
    },

    computed: {
        publicProfile() {
            return this.$store.getters["app/publicProfile"];
        },

        profile() {
            return this.$store.getters["auth/profile"];
        },

        initials() {
            return this.$initials(this.profile?.fullName);
        },

        profileUrl() {
            return this.profile?.profileImageUrl || "";
        },

        accountPlan() {
            return this.$store.getters["subscription/subscriptionInfo"]?.plan || "free";
        },

        publicShareUrl() {              
            if (!this.publicProfile?.slug) return "";          
            
            return `${envs.VUE_APP_CLIENT_URL}/public/${this.publicProfile?.slug}`;
        },

        checkboxMap: {
            get() {
                // map where each field's key maps to true/false
                const map = {};

                const buildMap = (items) => {
                    for (const item of items) {                        
                        if (item.children.length) {                            
                            // Parent value = all children selected
                            map[item.key] = item.children.every(
                                (child) => this.selectedFields.includes(child.key),
                            );
                            buildMap(item.children);
                        } else {                            
                            map[item.key] = this.selectedFields.includes(item.key);
                        }
                    }
                };

                buildMap(this.profileSelections);                
                return map;
            },

            set(updatedMap) {
                let newSelection = [...this.selectedFields];

                const updateFromMap = (items) => {
                    for (const item of items) {
                        if (item.children) {
                            // If parent changed → update all children
                            if (updatedMap[item.key] !== undefined) {
                                if (updatedMap[item.key]) {
                                    // Add all children keys
                                    console.log({ item, updatedMap });
                                    
                                    item.children.forEach((child) => {
                                        if (!newSelection.includes(child.key)) {
                                            newSelection.push(child.key);
                                        }
                                    });
                                } else {
                                    // Remove all children keys
                                    newSelection = newSelection.filter(
                                        (key) => !item.children.some((child) => child.key === key),
                                    );
                                }
                            }
                            updateFromMap(item.children);
                        } else {
                            // Single field update
                            if (updatedMap[item.key] && !newSelection.includes(item.key)) {
                                newSelection.push(item.key);
                            } else if (!updatedMap[item.key] && newSelection.includes(item.key)) {
                                newSelection = newSelection.filter((key) => key !== item.key);
                            }
                        }
                    }
                };   
            },   
        },
        
        pageErrMsg() {
            if (this.errMsg["fetchData"].includes("Not available on your plan")) {
                return {
                    title: "This is a paid feature",
                    sub: "Upgrade to a plus plan to get access to this feature",
                };
            }

            return {
                title: "Page error",
                sub: "",
            };
        },
    },

    watch: {
        showModal: {
            async handler(newVal) {
                if (!this.publicProfile && this.accountPlan === "plus") {
                    await this.getPublicData(); 
                }
            },
        },

        publicProfile: {
            async handler(newVal) {
                if (this.accountPlan === "plus") {
                    if (!newVal ) {
                        this.pageActive = false;
                        this.isFeatDisabled = true;
                    } else {
                        this.pageActive = true;
                        this.isFeatDisabled = false;
                    }
                }
            },
            deep: true,
        },
    },

    async mounted() {
        if (this.profileSelections.length) {
            this.setInitialValues(this.profileSelections);
            // this.selectedFields = this.selectedFields.filter(x => )
        }
    },

    methods: {
        transformDataForChecklist(objData) {
            return Object.entries(objData).map(([key, values]) => {
                if (!values) return null;

                // Leaf nodes (toggleable)
                if (["string", "array"].includes(values.type)) {
                    return {
                        label: values.name,
                        selected: values.selected,
                        disabled: !values.selectable,
                        children: [],
                        key: key,
                    };
                }

                // Grouped items (with children)
                if (values.type === "object" && values.children) {
                    return {
                        label: values.name,
                        selected: true, // parent group not toggleable by default
                        disabled: false,  // groups are just labels
                        children: this.transformDataForChecklist(values.children),
                        key,
                    };
                }

                return null;
            }).filter(Boolean); // Remove nulls
        },

        setInitialValues(arr) {
            for (let item of arr) {                
                if (!item.children.length) {
                    if (item.selected) {
                        this.selectedFields.push(item.key);
                    }
                } else {
                    this.selectedFields.push(item.key);
                    this.setInitialValues(item.children);
                }
            }
        },

        closeModal() {
            this.$emit("cancel");
        },

        onEnter(el) {
            // Force reflow to ensure proper animation
            el.offsetHeight;
            
            // Add a small delay to prevent conflicts with other modal transitions
            this.$nextTick(() => {
                el.classList.add("modal-enter-active");
            });
        },

        onLeave(el) {
            el.classList.add("modal-leave-active");
        },

        closeAndOpenBillings() {
            this.$emit("cancel");
            this.$router.push("/dashboard/settings/payments-and-billings");
        },

        async getPublicData() {
            try {                
                this.isFeatDisabled = false;

                this.pageState = "data_loading";
                delete this.errMsg["fetchData"];
                
                await this.$store.dispatch("app/fetchPublicProfile");
                this.pageState = "";
            } catch (error) {                                                
                this.pageState = "data_error";
                const errMsg = error?.response?.data?.message || error?.message;                
                this.errMsg["fetchData"] = errMsg;
            }
        },

        async saveProfile(activate = undefined) {
            try {
                this.pageState = "save_updates_loading";
                let finalFields = [];

                if (this.selectedFields.includes("location")) {
                    finalFields = [ ...finalFields, ...["city", "country"]];
                }

                // selectedFields: ["fullName","occupation","completedProjectCount","onboardedClientsCount","bio","location","contactInformation","email","phoneNumber","portfolio","socials","skills","links"]
                finalFields = [ ...finalFields, ...this.selectedFields.filter(x => !["contactInformation", "portfolio", "location"].includes(x))];

                const payload = {
                    fields: finalFields,
                    activate, 
                };

                if (!activate) {
                    payload.isActive = this.pageActive;
                }
                
                await this.$store.dispatch("app/savePublicProfileUpdates", payload);
                this.pageState = "";
            } catch (error) {
                this.pageState = "save_updates_err";
                const errMsg = error?.response?.data?.message || error?.message;                
                this.$toast.error(errMsg);
            }
        },

        async handleFetchDataErr() {
            if (this.errMsg["fetchData"].includes("Not available on your plan")) return;

            await this.getPublicData();
        },

        handleCopyNoteContent(item) {

        },
    },
};
</script>

<style lang="scss" scoped>
    .z_p_header {
        border-radius: 30px;
        align-items: center;
        display: flex;
        margin: 1.5rem 1.5rem 1rem;
        border: 1px solid #eee;
    }

    .profile {
        &-wrap {
            gap: 25px 0px;
            display: grid;
        }

        &-title {
            font-size: 32px;
            font-weight: 700;
            color: #2C3E50;
            margin-bottom: 8px;
            margin-top: 20px;
        }

        &-stats {
            color: #7F8C8D;
            font-size: 14px;
        }

        &-card {
            background: white;
            padding: 30px;
            border-radius: 12px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
            text-align: left;
            width: 100%;

            h3 {
                font-size: 20px;
                font-weight: 600;
                color: #2C3E50;
                margin-bottom: 20px;
            }

            p, span {
                color: #5A6C7D;
                line-height: 1.6;
                margin-bottom: 16px;
                font-size: 0.875rem;
            }
        }

        &-icon {
            --tw-border-opacity: 1;
            border-color: rgb(255 255 255 / var(--tw-border-opacity, 1));
            border-width: 4px;
            border-radius: 9999px;
            width: 6rem;
            height: 6rem;
            box-shadow: 0 1rem 3rem rgba(0, 0, 0, 0.175) !important;
            --tw-shadow: 0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1);
            --tw-shadow-colored: 0 10px 15px -3px var(--tw-shadow-color), 0 4px 6px -4px var(--tw-shadow-color);
            box-shadow: var(--tw-ring-offset-shadow, 0 0 #0000), var(--tw-ring-shadow, 0 0 #0000), var(--tw-shadow);
            border-style: solid;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 20px;
            font-weight: 600;
            color: #4a3b31;
        }

        &-select {
           white-space: nowrap;
            text-overflow: ellipsis;
            max-width: 100px;
            border: 1px solid red;
        }

        &-sidebar {
            position: fixed;
            top: 8%;
            bottom: 0;
            width: 240px;
            height: auto;
            max-height: fit-content;
        }

        &-content {
            margin-left: 240px;
            height: calc(100% - 20px);
            // border: 1px solid red;
            overflow-y: scroll;
            padding-left: 15px;
            border-left: 1px solid #eee
        }
    }

    .main-container {
        margin: 0 auto; 
        background: linear-gradient(90deg, #fdd6cd, #edb9ff);
        border-radius: 30px;
        overflow-y: scroll;
        height: 100%;
    }

    .tag {
        background-color: #333333;
        color: white;
    }

    ::-webkit-scrollbar {
        width: 8px;
    }

    ::-webkit-scrollbar-track {
        background: #f1f1f1;
    }

    ::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 4px;
    }

    ::-webkit-scrollbar-thumb:hover {
        background: #555;
    }

    .modal {
        &__dialog__profile {
            height: 80%;
            padding: 20px;
            width: 1060px;
            max-width: 1060px;
            
            .modal__header {
                display: flex;
                justify-content: flex-end;
                align-items: baseline;
                padding-top: 0rem;
                padding-right: 0.5rem;
            }
        }

        &__body {
            width: 100%;
            padding: 0;
        }

        &-wrap {
            background: #fff;
            border-radius: 20px;
        }
    }
</style>